import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SocialButtons from "@/components/SocialButtons";
import HomePage from "@/pages/HomePage";
import ProcessPage from "@/pages/ProcessPage";
import ServicesPage from "@/pages/ServicesPage";
import ContactPage from "@/pages/ContactPage";
import GalleryPage from "@/pages/GalleryPage";
import CalculatorPage from "@/pages/CalculatorPage";
import AdminPage from "@/pages/AdminPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import AdminVehiclesPage from "@/pages/AdminVehiclesPage";
import AdminVideosPage from "@/pages/AdminVideosPage";
import AdminAnalyticsPage from "@/pages/AdminAnalyticsPage";
import AvailableVehiclesPage from "@/pages/AvailableVehiclesPage";
import PaymentPage from "@/pages/PaymentPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import { useEffect, useRef } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Carbon background component
const CarbonBackground = () => (
  <div 
    className="fixed inset-0 pointer-events-none"
    style={{
      backgroundImage: "url('/assets/Fast & Car/Fond Carbon.jpeg')",
      backgroundSize: '300px 300px',
      backgroundRepeat: 'repeat',
      opacity: 0.15,
      zIndex: -1,
    }}
    aria-hidden="true"
  />
);

// Protected route component
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem("adminToken");
  const location = useLocation();
  
  if (!token) {
    return <Navigate to="/espace-pro-fastandcar" state={{ from: location }} replace />;
  }
  
  return children;
};

// Analytics tracker component
const AnalyticsTracker = () => {
  const location = useLocation();
  const sessionIdRef = useRef(null);
  const sessionStartRef = useRef(Date.now());
  const pagesVisitedRef = useRef(0);

  useEffect(() => {
    // Track page view
    const trackPageView = async () => {
      try {
        const response = await axios.post(`${API}/analytics/pageview`, {
          page: location.pathname,
          referrer: document.referrer || null,
          user_agent: navigator.userAgent,
        });
        if (response.data.session_id && !sessionIdRef.current) {
          sessionIdRef.current = response.data.session_id;
        }
        pagesVisitedRef.current += 1;
      } catch (error) {
        // Silent fail for analytics
        console.debug("Analytics tracking failed:", error);
      }
    };

    // Don't track admin pages
    if (!location.pathname.startsWith("/espace-pro-fastandcar")) {
      trackPageView();
    }
  }, [location.pathname]);

  useEffect(() => {
    // Track session end when user leaves
    const handleBeforeUnload = () => {
      if (sessionIdRef.current) {
        const duration = Math.round((Date.now() - sessionStartRef.current) / 1000);
        // Use sendBeacon for reliability on page unload
        navigator.sendBeacon(
          `${API}/analytics/session-end`,
          JSON.stringify({
            session_id: sessionIdRef.current,
            duration_seconds: duration,
            pages_visited: pagesVisitedRef.current,
          })
        );
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  return null;
};

function App() {
  return (
    <div className="App min-h-screen bg-[#050505]">
      <CarbonBackground />
      <BrowserRouter>
        <AnalyticsTracker />
        <div className="relative z-10">
          <Header />
          <main>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/process" element={<ProcessPage />} />
              <Route path="/services" element={<ServicesPage />} />
              <Route path="/galerie" element={<GalleryPage />} />
              <Route path="/simulateur" element={<CalculatorPage />} />
              <Route path="/vehicules-disponibles" element={<AvailableVehiclesPage />} />
              <Route path="/vehicules-disponibles/:vehicleId" element={<AvailableVehiclesPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/paiement" element={<PaymentPage />} />
              <Route path="/paiement/success" element={<PaymentPage />} />
              <Route path="/espace-pro-fastandcar" element={<AdminLoginPage />} />
              <Route path="/espace-pro-fastandcar/dashboard" element={
                <ProtectedRoute>
                  <AdminPage />
                </ProtectedRoute>
              } />
              <Route path="/espace-pro-fastandcar/analytics" element={
                <ProtectedRoute>
                  <AnalyticsPage />
                </ProtectedRoute>
              } />
              <Route path="/espace-pro-fastandcar/vehicules" element={
                <ProtectedRoute>
                  <AdminVehiclesPage />
                </ProtectedRoute>
              } />
              <Route path="/espace-pro-fastandcar/videos" element={
                <ProtectedRoute>
                  <AdminVideosPage />
                </ProtectedRoute>
              } />
              <Route path="/espace-pro-fastandcar/stats" element={
                <ProtectedRoute>
                  <AdminAnalyticsPage />
                </ProtectedRoute>
              } />
            </Routes>
          </main>
          <Footer />
          <SocialButtons />
        </div>
        <Toaster position="bottom-right" theme="dark" />
      </BrowserRouter>
    </div>
  );
}

export default App;
